import { ImsCostingMethod } from "@redotech/crdb-enums/inventory/ims-costing-method";
import { COST_MOVEMENTS } from "@redotech/redo-inventory-common/cost/cost-movement";
import { costConsumptionIdFormat } from "@redotech/redo-inventory-id";
import {
  productFamilyIdFormat,
  productIdFormat,
} from "@redotech/redo-product-id";
import { AdvancedFilterDataSchema } from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { SortDirectionSchema } from "@redotech/redo-views-common/table";
import * as z from "zod";
import { FractionalAmountSchema } from "./cost-report-amount";

export const CogsReportSortKeySchema = z.enum([
  "date",
  "quantity",
  "extendedCost",
]);
export type CogsReportSortKey = z.infer<typeof CogsReportSortKeySchema>;

const CogsReportPageRowSchema_ = z.object({
  id: costConsumptionIdFormat.schema,
  occurredAt: z.date(),
  referenceId: z.string(),
  /** The order's Redo id when it has one, for a link; the name shows either way. */
  order: z
    .object({ redoOrderId: z.string().nullable(), name: z.string().nullable() })
    .nullable(),
  productId: productIdFormat.schema,
  productFamilyId: productFamilyIdFormat.schema.nullable(),
  sku: z.string().nullable(),
  title: z.string(),
  viaBomSku: z.string().nullable(),
  movement: z.enum(COST_MOVEMENTS),
  reasonName: z.string().nullable(),
  quantity: z.number().int(),
  layerReceivedAt: z.date().nullable(),
  layerSource: z.string().nullable(),
  unitCostFractional: FractionalAmountSchema.nullable(),
  extendedCostFractional: FractionalAmountSchema.nullable(),
  currency: z.string(),
  costingMethod: z.enum(ImsCostingMethod),
  settled: z.boolean(),
});
export const CogsReportPageRowSchema: z.ZodType<CogsReportPageRow> =
  CogsReportPageRowSchema_;
export interface CogsReportPageRow extends z.infer<
  typeof CogsReportPageRowSchema_
> {}

/**
 * Filters: the report period (first DATE filter), "sku", and "movement"
 * (ARRAY_TO_STRING, values from COST_MOVEMENTS, ANY_OF or NONE_OF).
 */
export const getCogsReportRows = {
  name: "getCogsReportRows" as const,
  input: z.object({
    page: z.number().int().min(0).default(0),
    pageSize: z.number().int().min(1).max(1000),
    sortBy: CogsReportSortKeySchema.optional(),
    sortDirection: SortDirectionSchema.optional(),
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({ rows: z.array(CogsReportPageRowSchema) }),
};

/**
 * total counts the filtered rows. cogsFractional and unitsSold cover sale
 * rows; unsettledFractional covers rows still waiting on a receipt.
 */
export const getCogsReportSummary = {
  name: "getCogsReportSummary" as const,
  input: z.object({
    advancedFiltersData: z.array(AdvancedFilterDataSchema).optional(),
  }),
  output: z.object({
    total: z.number().int(),
    cogsFractional: FractionalAmountSchema,
    unitsSold: z.number().int(),
    unsettledFractional: FractionalAmountSchema,
    currency: z.string(),
  }),
};
